import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import UploadCompletionModal, { ProcessingOptions } from './UploadCompletionModal';
import { VideoApi } from '@/lib/api/videoApi';
import { VideoMetadata } from '@/lib/api/types';

export default function VideoUploader() {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStep, setUploadStep] = useState('');
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [uploadedVideoId, setUploadedVideoId] = useState<string | null>(null);
  const [uploadedVideoName, setUploadedVideoName] = useState('');
  const [passcode, setPasscode] = useState('');
  const [showPasscodeInput, setShowPasscodeInput] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const { session } = useAuth();
  const videoApi = new VideoApi();

  const BETA_PASSCODE = 'BETA2025';
  const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB
  const MAX_FILE_SIZE_WITH_PASSCODE = 500 * 1024 * 1024; // 500MB

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };
  
  const handleDragLeave = () => {
    setIsDragging(false);
  };
  
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileUpload(files[0]);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFileUpload(e.target.files[0]);
    }
  };

  const handleFileUpload = async (file: File) => {
    // Check if file is a video
    if (!file.type.startsWith('video/')) {
      toast({
        title: "Invalid file type",
        description: "Please upload a video file.",
        variant: "destructive"
      });
      return;
    }

    // Check file size
    const fileSizeMB = file.size / 1024 / 1024;
    const hasValidPasscode = passcode === BETA_PASSCODE;
    
    if (file.size > MAX_FILE_SIZE && !hasValidPasscode) {
      if (file.size <= MAX_FILE_SIZE_WITH_PASSCODE) {
        // File is larger than 100MB but smaller than 500MB - show passcode input
        setShowPasscodeInput(true);
        toast({
          title: "Large file detected",
          description: `File size ${fileSizeMB.toFixed(2)}MB exceeds the 100MB limit. Enter the beta passcode to upload files up to 500MB.`,
          variant: "default"
        });
        return;
      } else {
        // File is too large even with passcode
        toast({
          title: "File too large",
          description: `File size ${fileSizeMB.toFixed(2)}MB exceeds the maximum allowed size of 500MB.`,
          variant: "destructive"
        });
        return;
      }
    }
    
    try {
      setIsUploading(true);
      setUploadedVideoName(file.name);
      setUploadProgress(0);
      setUploadStep('Preparing upload...');
      
      const metadata: VideoMetadata = {
        title: file.name,
        description: '',
        file_name: file.name,
        file_size: file.size,
        content_type: file.type
      };
      
      if (session?.access_token) {
        console.log('Using real backend API with token');
        
        // Upload with default processing options and passcode
        const video = await videoApi.uploadVideo(file, metadata, session.access_token, {
          generate_transcript: true,
          detect_speakers: true,
          extract_objects: true
        }, passcode);
        
        // Update progress based on VideoApi's progress tracking
        const progressInterval = setInterval(() => {
          const progress = videoApi.getUploadProgress(video.id);
          if (progress) {
            setUploadProgress(progress.progress);
            if (progress.status === 'failed') {
              throw new Error(progress.error || 'Upload failed');
            }
          }
        }, 100);
        
        // Step 3: Complete
        setUploadStep('Finalizing upload...');
        setUploadProgress(100);
        
        // Store the uploaded video ID
        setUploadedVideoId(video.id);
        
        clearInterval(progressInterval);
        
        // Show success message with the video details we already have
        toast({
          title: "Upload successful",
          description: `Your video "${video.title}" has been uploaded and is being processed.`,
        });
        
      } else {
        console.log('No Supabase token, using mock upload...');
        
        // Simulate upload progress for mock API
        const progressInterval = setInterval(() => {
          setUploadProgress(prev => {
            const nextProgress = prev + Math.random() * 15;
            return nextProgress > 98 ? 98 : nextProgress;
          });
        }, 500);
        
        // Mock upload
        const mockVideo = {
          id: 'mock-' + Date.now(),
          title: file.name,
          description: '',
          file_name: file.name,
          file_size: file.size,
          status: 'COMPLETED',
          gcs_path: 'mock/path',
          playback_url: 'https://example.com/mock-video.mp4',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };
        
        clearInterval(progressInterval);
        setUploadProgress(100);
        setUploadedVideoId(mockVideo.id);
      }
      
      // Show completion modal after a short delay
      setTimeout(() => {
        setShowCompletionModal(true);
      }, 500);
      
    } catch (error) {
      setIsUploading(false);
      setUploadProgress(0);
      setUploadStep('');
      
      toast({
        title: "Upload failed",
        description: error instanceof Error ? error.message : "There was an error uploading your video.",
        variant: "destructive"
      });
    }
  };

  const handleProcessNow = async (options: ProcessingOptions) => {
    try {
      const supabaseToken = session?.access_token;
      if (!supabaseToken) {
        throw new Error('No authentication token available');
      }
      
      if (!uploadedVideoId) {
        throw new Error('No video ID available for processing');
      }
      
      console.log('Processing video with options:', {
        generate_transcript: options.generateTranscript,
        detect_speakers: options.detectSpeakers,
        extract_objects: options.extractObjects
      });
      
      // Call the process video API
      await videoApi.processVideo(uploadedVideoId, {
        generate_transcript: options.generateTranscript,
        detect_speakers: options.detectSpeakers,
        extract_objects: options.extractObjects
      }, supabaseToken);
      
      toast({
        title: "Processing started",
        description: "Your video is being processed. You'll be notified when it's complete.",
      });
      
      setShowCompletionModal(false);
    } catch (error) {
      toast({
        title: "Processing failed",
        description: error instanceof Error ? error.message : "There was an error processing your video.",
        variant: "destructive"
      });
    }
  };

  const handleProcessLater = () => {
    toast({
      title: "Processing scheduled",
      description: `Your video "${uploadedVideoName}" will be processed later.`
    });
    
    resetUploader();
  };
  
  const resetUploader = () => {
    setIsUploading(false);
    setUploadProgress(0);
    setShowCompletionModal(false);
    setUploadedVideoId(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  const handleModalClose = () => {
    setShowCompletionModal(false);
    resetUploader();
  };
  
  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const handlePasscodeSubmit = () => {
    if (passcode === BETA_PASSCODE) {
      setShowPasscodeInput(false);
      toast({
        title: "Passcode accepted",
        description: "You can now upload files up to 500MB.",
        variant: "default"
      });
    } else {
      toast({
        title: "Invalid passcode",
        description: "Please enter the correct beta passcode.",
        variant: "destructive"
      });
    }
  };

  const handlePasscodeCancel = () => {
    setShowPasscodeInput(false);
    setPasscode('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <>
      <div
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
          isDragging ? 'border-video-blue bg-blue-50' : 'border-border'
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          accept="video/*"
          className="hidden"
        />
        
        {isUploading ? (
          <div className="py-8">
            <div className="mb-2 text-sm font-medium">
              {uploadStep || 'Uploading...'}
            </div>
            <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
              <div 
                className="h-full bg-video-blue transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
            <div className="mt-2 text-sm text-muted-foreground">
              {Math.round(uploadProgress)}% complete
            </div>
            {session?.access_token && (
              <div className="mt-1 text-xs text-green-600">
                Using real backend API
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="mx-auto w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8 text-muted-foreground">
                <path strokeLinecap="round" d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z" />
              </svg>
            </div>
            <h3 className="font-medium text-lg mb-2">Upload a video</h3>
            <p className="text-muted-foreground mb-4">Drag and drop a video file here, or click to browse</p>
            {session?.access_token && (
              <p className="text-xs text-green-600 mb-2">✓ Connected to real backend API</p>
            )}
            <Button onClick={handleBrowseClick}>Browse files</Button>
          </>
        )}
      </div>

      {/* Passcode Input Modal */}
      {showPasscodeInput && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Beta Passcode Required</h3>
            <p className="text-sm text-gray-600 mb-4">
              Your file exceeds the 100MB limit. Enter the beta passcode to upload files up to 500MB.
            </p>
            <div className="space-y-4">
              <div>
                <Label htmlFor="passcode">Beta Passcode</Label>
                <Input
                  id="passcode"
                  type="password"
                  value={passcode}
                  onChange={(e) => setPasscode(e.target.value)}
                  placeholder="Enter passcode"
                  className="mt-1"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handlePasscodeSubmit();
                    }
                  }}
                />
              </div>
              <div className="flex space-x-2">
                <Button onClick={handlePasscodeSubmit} className="flex-1">
                  Submit
                </Button>
                <Button onClick={handlePasscodeCancel} variant="outline" className="flex-1">
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <UploadCompletionModal
        isOpen={showCompletionModal}
        onClose={handleModalClose}
        videoId={uploadedVideoId}
        videoName={uploadedVideoName}
        onProcessNow={handleProcessNow}
        onProcessLater={handleProcessLater}
      />
    </>
  );
}
